import { useTasksTranslations } from '@/hooks/use-translations'
import { useLocale } from '@/lib/i18n-client'

// Fallback translations for when translations fail to load
const FALLBACK_TRANSLATIONS: Record<string, string> = {
  "notifications.task_deleted": "Task deleted",
  "notifications.task_deleted_description": "Task has been deleted",
  "notifications.tasks_deleted": "Tasks deleted",
  "notifications.tasks_deleted_description": "{count} tasks have been deleted",
  "notifications.tasks_restored": "Tasks restored",
  "notifications.tasks_restored_description": "{count} tasks have been restored",
  "notifications.undo": "Undo",
  "notifications.task_restored": "Task restored",
  "notifications.failed_to_delete_task": "Failed to delete task",
  "notifications.unexpected_error": "Unexpected error",
  "notifications.task_archived": "Task archived",
  "notifications.task_archived_description": "Task has been archived",
  "notifications.task_unarchived": "Task unarchived",
  "notifications.task_unarchived_description": "Task has been unarchived",
  "notifications.tasks_archived": "Tasks archived",
  "notifications.tasks_archived_description": "tasks have been archived",
  "notifications.tasks_unarchived": "Tasks unarchived",
  "notifications.tasks_unarchived_description": "{count} tasks have been unarchived",
  "notifications.failed_to_archive_task": "Failed to archive task",
  "notifications.failed_to_unarchive_task": "Failed to unarchive task",
  "notifications.task_scheduled": "Task scheduled",
  "notifications.task_scheduled_description": "Task has been scheduled",
  "notifications.task_unscheduled": "Task unscheduled",
  "notifications.task_unscheduled_description": "Removed scheduling for {taskTitle}",
  "task": "Task",
  "notifications.task_failed": "Task operation failed",
  "notifications.task_assigned": "Task assigned",
  "notifications.task_assigned_description": "Task has been assigned",
  "notifications.task_unassigned": "Task unassigned",
  "notifications.task_unassigned_description": "Task has been unassigned",
}

/**
 * Get fallback translation when the main translation system fails
 */
function getFallbackTranslation(key: string, params?: Record<string, string | number>): string {
  const fallback = FALLBACK_TRANSLATIONS[key] || key
  
  if (params && typeof params === 'object') {
    let result = fallback
    Object.keys(params).forEach(paramKey => {
      const placeholder = `{${paramKey}}`
      result = result.replace(new RegExp(placeholder, 'g'), String(params[paramKey]))
    })
    return result
  }
  
  return fallback
}

// Type definition that matches the store requirements
type TranslationFunctions = {
  // Delete task translations
  task_deleted: string
  task_deleted_description: (title: string) => string
  tasks_deleted: string
  tasks_deleted_description: (count: number) => string
  tasks_restored: string
  tasks_restored_description: (count: number) => string
  undo: string
  task_restored: (title: string) => string
  failed_to_delete_task: string
  unexpected_error: string
  
  // Archive task translations
  task_archived: string
  task_archived_description: (title: string) => string
  task_unarchived: string
  task_unarchived_description: (title: string) => string
  tasks_archived: string
  tasks_archived_description: (count: number) => string
  tasks_unarchived: string
  tasks_unarchived_description: (count: number) => string
  failed_to_archive_task: string
  failed_to_unarchive_task: string
  
  // Schedule task translations
  task_scheduled: string
  task_scheduled_description: (title: string, date: string, time: string) => string
  task_unscheduled: string
  task_unscheduled_description: (title: string) => string
  task: string
  task_failed: string
  
  // Assign task translations
  task_assigned: string
  task_assigned_description: (title: string, assigneeName: string) => string
  task_unassigned: string
  task_unassigned_description: (title: string, assigneeName: string) => string
}

/**
 * Hook to get task store translations in the format expected by the store methods
 */
export function useTaskStoreTranslations() {
  const t = useTasksTranslations()
  const locale = useLocale()

  // Fallback translations to prevent undefined errors
  const safeT = (key: string, params?: Record<string, string | number>) => {
    try {
      const result = t(key, params)
      if (result === key || result === undefined || result === null) {
        // Return fallback if translation is missing
        return getFallbackTranslation(key, params)
      }
      return result
    } catch (error) {
      console.warn(`Translation error for key: ${key}`, error)
      return getFallbackTranslation(key, params)
    }
  }

  return {
    translations: {
      // Delete task translations
      task_deleted: safeT("notifications.task_deleted"),
      task_deleted_description: (taskTitle: string) => safeT("notifications.task_deleted_description", { taskTitle }),
      tasks_deleted: safeT("notifications.tasks_deleted"),
      tasks_deleted_description: (count: number) => safeT("notifications.tasks_deleted_description", { count }),
      tasks_restored: safeT("notifications.tasks_restored"),
      tasks_restored_description: (count: number) => safeT("notifications.tasks_restored_description", { count }),
      undo: safeT("notifications.undo"),
      task_restored: (taskTitle: string) => safeT("notifications.task_restored", { taskTitle }),
      failed_to_delete_task: safeT("notifications.failed_to_delete_task"),
      unexpected_error: safeT("notifications.unexpected_error"),
      
      // Archive task translations
      task_archived: safeT("notifications.task_archived"),
      task_archived_description: (taskTitle: string) => safeT("notifications.task_archived_description", { taskTitle }),
      task_unarchived: safeT("notifications.task_unarchived"),
      task_unarchived_description: (taskTitle: string) => safeT("notifications.task_unarchived_description", { taskTitle }),
      tasks_archived: safeT("notifications.tasks_archived"),
      tasks_archived_description: (count: number) => safeT("notifications.tasks_archived_description", { count }),
      tasks_unarchived: safeT("notifications.tasks_unarchived"),
      tasks_unarchived_description: (count: number) => safeT("notifications.tasks_unarchived_description", { count }),
      failed_to_archive_task: safeT("notifications.failed_to_archive_task"),
      failed_to_unarchive_task: safeT("notifications.failed_to_unarchive_task"),
      
      // Schedule task translations
      task_scheduled: safeT("notifications.task_scheduled"),
      task_scheduled_description: (taskTitle: string, date: string, time: string) => {
        const scheduledDate = time ? `${date} at ${time}` : date
        return safeT("notifications.task_scheduled_description", { taskTitle, scheduledDate })
      },
      task_unscheduled: safeT("notifications.task_unscheduled"),
      task_unscheduled_description: (taskTitle: string) => safeT("notifications.task_unscheduled_description", { taskTitle }),
      task: safeT("task") || "Task", // Fallback if translation is missing
      task_failed: safeT("notifications.task_failed") || "Task operation failed",
      
      // Assign task translations
      task_assigned: safeT("notifications.task_assigned"),
      task_assigned_description: (taskTitle: string, assigneeName: string) => safeT("notifications.task_assigned_description", { taskTitle, assigneeName }),
      task_unassigned: safeT("notifications.task_unassigned"),
      task_unassigned_description: (taskTitle: string, assigneeName: string) => safeT("notifications.task_unassigned_description", { taskTitle, assigneeName }),
    } as TranslationFunctions,
    locale,
  }
}
